/**
 * Creates a Promise that is resolved with an record of results when all of the provided Promises
 * resolve, or rejected when any Promise is rejected.
 *
 * @param values An record of Promises.
 * @returns A new Promise.
 * @example joinPromiseRecord({ first: Promise.resolve("a"), second: Promise.resolve(2) })
 * // Result same as Promise.resolve({ first: "a", second: 2 })
 * @remarks Processes all own string and symbol keys, including non-enumerable properties.
 */
export const joinPromiseRecord = <T extends Record<string | number | symbol, unknown>>(promises: T): Promise<{ [K in keyof T]: Awaited<T[K]> }> => {
  return new Promise((resolve, reject) => {
    const keyList = Reflect.ownKeys(promises);
    const l = keyList.length;
    if (l > 0) {
      const result: Record<string | symbol, unknown> = {};
      let completed: number = 0;
      const resolveAllFn = (key: string | symbol, value: Awaited<unknown>): void => {
        Object.defineProperty(result, key, { value, configurable: true, enumerable: true, writable: true });
        if (++completed === l) resolve(<{ [K in keyof T]: Awaited<T[K]> }>result);
      };
      for (const key of keyList) {
        Promise.resolve(promises[key]).then((value: unknown) => resolveAllFn(key, value), reject);
      }
    } else {
      resolve(<ReturnType<typeof joinPromiseRecord<T>>>{});
    }
  });
};
